import React from "react";
import { Link } from "react-router-dom";
import { Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import * as moment from "moment";

import PSEARatingBadge from "./PSEARatingBadge";
import { PSEA_CORE_STANDARDS } from "./pseaCoreStandards";
import { PSEA_TOTAL_STANDARDS, pseaAnswer } from "./pseaScoring";

/**
 * Full detail of a single assessment-history entry. The timeline is deliberately
 * condensed, so this is where the entry is shown in full: the score behind the
 * rating, who recorded it and their email, the untruncated justification, and
 * the 8 core standards as they were answered at that point in time.
 */

/** How each answer reads in the snapshot list. */
const ANSWER_DISPLAY: Record<string, { label: string; tone: string }> = {
  yes: { label: "Met · 1 point", tone: "success" },
  na: { label: "Not applicable · 1 point", tone: "success" },
  no: { label: "Not met · 0 points", tone: "danger" },
};

const NOT_ANSWERED = { label: "Not answered", tone: "muted" };

/**
 * One core standard as captured in the snapshot, aligned to the canonical
 * 8-standard order so the titles come from the current definitions while the
 * answers come from the frozen record.
 */
const SnapshotStandard = ({ standard, cs }: { standard: any; cs: any }) => {
  const answer = pseaAnswer(cs);
  // Standard 8 is about corrective action for past SEA allegations, so "Not
  // applicable" reads as "No Allegations" there specifically.
  const display = answer
    ? answer === "na" && standard.number === 8
      ? { ...ANSWER_DISPLAY.na, label: "No Allegations · 1 point" }
      : ANSWER_DISPLAY[answer]
    : NOT_ANSWERED;
  const evidence: string[] = cs?.evidence || [];
  const docs: any[] = cs?.supportingDocuments || [];

  return (
    <div className="border rounded p-2">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span
          className={`psea-standard-number avatar-xs d-flex align-items-center justify-content-center rounded-circle fw-bold fs-11 flex-shrink-0 ${
            display.tone === "muted"
              ? "bg-body text-muted border"
              : `bg-${display.tone}-subtle text-${display.tone}`
          }`}
        >
          {standard.number}
        </span>
        <span className="fw-semibold fs-13 flex-grow-1">{standard.title}</span>
        <span
          className={
            display.tone === "muted"
              ? "badge bg-light text-muted border fs-11"
              : `badge bg-${display.tone}-subtle text-${display.tone} fs-11`
          }
        >
          {display.label}
        </span>
      </div>

      {evidence.length > 0 ? (
        <ul className="list-unstyled mb-0 mt-2 ps-4 vstack gap-1">
          {evidence.map((e, i) => (
            <li key={i} className="fs-12 d-flex align-items-start">
              <i className="ri-checkbox-circle-line text-success me-2 flex-shrink-0"></i>
              <span>{e}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {cs?.otherSpecify ? (
        <p className="fs-12 text-muted mb-0 mt-2 ps-4">
          <span className="fw-medium">Other: </span>
          {cs.otherSpecify}
        </p>
      ) : null}

      {docs.length > 0 ? (
        <div className="mt-2 ps-4 d-flex flex-wrap gap-2">
          {docs.map((doc: any, i: number) => (
            <Link
              key={i}
              to={`${process.env.REACT_APP_FILES_URL}${doc?.url}`}
              target="_blank"
              className="badge bg-light text-body border fw-normal fs-11 text-truncate"
              style={{ maxWidth: "14rem" }}
              title={doc?.name}
            >
              <i className="ri-attachment-2 align-bottom me-1"></i>
              {doc?.name}
            </Link>
          ))}
        </div>
      ) : null}

      {cs?.comment ? (
        <p
          className="fs-12 text-muted mb-0 mt-2 ps-4"
          style={{ whiteSpace: "pre-wrap" }}
        >
          <span className="fw-medium">Comment: </span>
          {cs.comment}
        </p>
      ) : null}
    </div>
  );
};

const Field = ({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="d-flex align-items-start gap-3">
    <div className="flex-shrink-0">
      <div className="avatar-xs">
        <div className="avatar-title bg-light text-muted rounded fs-16">
          <i className={icon}></i>
        </div>
      </div>
    </div>
    <div className="flex-grow-1 overflow-hidden">
      <p className="text-uppercase text-muted fw-medium fs-11 mb-1">{label}</p>
      <div className="fs-14 fw-medium text-break">{children}</div>
    </div>
  </div>
);

const dash = <span className="text-muted">-</span>;

const PSEAHistoryEntryModal = ({
  entry,
  isOpen,
  toggle,
  isLatest,
}: {
  entry: any;
  isOpen: boolean;
  toggle: () => void;
  isLatest?: boolean;
}) => {
  if (!entry) return null;

  const author = [entry?.createdby?.firstName, entry?.createdby?.lastName]
    .filter(Boolean)
    .join(" ");
  const email = entry?.createdby?.email;
  const score = entry?.overallScore;

  // Entries recorded before snapshots were captured have no core standards, so
  // the section explains the gap rather than showing 8 empty rows.
  const snapshot: any[] = Array.isArray(entry?.coreStandards)
    ? entry.coreStandards
    : [];
  const hasSnapshot = snapshot.length > 0;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" scrollable>
      <ModalHeader toggle={toggle}>
        <span className="d-flex align-items-center gap-2 flex-wrap">
          <span>{entry?.description || "Assessment updated"}</span>
          {isLatest ? (
            <span className="badge bg-success-subtle text-success fs-11">
              Current
            </span>
          ) : null}
        </span>
      </ModalHeader>
      <ModalBody>
        {/* Outcome recorded at this point in time */}
        <div className="border border-dashed rounded p-3 mb-4">
          <Row className="g-3 align-items-center">
            <Col sm={6}>
              <p className="text-uppercase text-muted fw-medium fs-11 mb-2">
                Overall Rating
              </p>
              <PSEARatingBadge rating={entry?.overallRating} />
            </Col>
            <Col sm={6}>
              <p className="text-uppercase text-muted fw-medium fs-11 mb-1">
                Score
              </p>
              <div className="fs-18 fw-semibold">
                {score === null || score === undefined ? (
                  dash
                ) : (
                  <React.Fragment>
                    {score}
                    <span className="text-muted fs-14">
                      {" "}
                      / {PSEA_TOTAL_STANDARDS}
                    </span>
                  </React.Fragment>
                )}
              </div>
            </Col>
          </Row>
        </div>

        <Row className="g-4">
          <Col md={6}>
            <Field icon="ri-time-line" label="Recorded On">
              {entry?.dateOfUpdate
                ? moment(new Date(entry.dateOfUpdate)).format(
                    "DD MMM YYYY, HH:mm"
                  )
                : dash}
            </Field>
          </Col>
          <Col md={6}>
            <Field icon="ri-calendar-line" label="Date of Assessment">
              {entry?.dateOfAssessment
                ? moment(new Date(entry.dateOfAssessment)).format("DD MMM YYYY")
                : dash}
            </Field>
          </Col>
          <Col md={6}>
            <Field icon="ri-user-line" label="Recorded By">
              {author ? (
                <React.Fragment>
                  <span className="d-block">{author}</span>
                  {email ? (
                    <a
                      href={`mailto:${email}`}
                      className="text-muted fs-13 fw-normal text-break"
                    >
                      {email}
                    </a>
                  ) : null}
                </React.Fragment>
              ) : (
                dash
              )}
            </Field>
          </Col>
          <Col md={6}>
            <Field icon="ri-government-line" label="Assessing Agency">
              {entry?.agency?.name ? (
                <span title={entry?.agency?.fullName || entry.agency.name}>
                  {entry.agency.name}
                </span>
              ) : (
                dash
              )}
            </Field>
          </Col>
        </Row>

        <div className="mt-4">
          <p className="text-uppercase text-muted fw-medium fs-11 mb-2">
            Justification
          </p>
          {entry?.justification ? (
            <div className="d-flex gap-2 p-3 rounded bg-light border-start border-3 border-primary">
              <i className="ri-chat-quote-line text-primary fs-16"></i>
              {/* Preserves the line breaks the author typed into the form. */}
              <div className="fs-13" style={{ whiteSpace: "pre-wrap" }}>
                {entry.justification}
              </div>
            </div>
          ) : (
            <p className="text-muted fst-italic fs-13 mb-0">
              No justification was recorded for this entry.
            </p>
          )}
        </div>

        <div className="mt-4">
          <p className="text-uppercase text-muted fw-medium fs-11 mb-2">
            Core Standards at This Point in Time
          </p>
          {hasSnapshot ? (
            <div className="vstack gap-2">
              {PSEA_CORE_STANDARDS.map((standard) => (
                <SnapshotStandard
                  key={standard.number}
                  standard={standard}
                  cs={snapshot.find(
                    (c: any) => Number(c?.standardNumber) === standard.number
                  )}
                />
              ))}
            </div>
          ) : (
            <p className="text-muted fst-italic fs-13 mb-0">
              The core standards were not captured for this entry. Only entries
              recorded from now on include them.
            </p>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PSEAHistoryEntryModal;
